<template>
  <div>
    <EmulatorSectionHeader title="Initialization" />
    <div>
      <div class="w-100 text-center mb-3">
        <button class="btn btn-white w-100 scope-option" @click="removeScope">
          <img :src="`/images/emulator/${scope.name}.svg`" />
          <span>{{ scope.label }}</span>
          <ShuffleIcon
            v-if="scopes.length > 1"
            size="15"
            class="ms-sm-2 text-muted scope-button-icon"
          />
        </button>
      </div>
      <div class="section" v-if="scope.products.length > 1">
        <label class="mb-1">Use Case</label>
        <p class="small text-muted mb-3">What tasks do you want to execute?</p>
        <div class="btn-group-toggle row gx-2 mb-4">
          <div v-for="product in scope.products" :key="product.name" class="col-12 my-1">
            <EmulatorProductSelector
              v-if="product.generallyAvailable || featureUniqueForAtomic"
              :product="product"
              :products="products"
              @toggle="toggleProduct"
            />
          </div>
        </div>
      </div>

      <div class="section" v-if="productsIncludeDeposit">
        <label class="mb-1">Deposit Accounts</label>
        <p class="small text-muted mb-3">
          Prompt the user to choose from their two available deposit accounts.
        </p>
        <CustomSwitch
          :value="useMultipleAccounts"
          label="Multiple Accounts"
          @input="toggleUseMultipleAccounts"
        />
      </div>

      <div class="section" v-if="productsIncludeTax">
        <label class="mb-1">Tax Forms</label>
        <p class="small text-muted mb-3">Select which forms to pull.</p>
        <div class="btn-group-toggle row gx-2 mb-4">
          <div class="col">
            <label
              class="btn w-100 btn-white"
              :class="{ active: taxForms.includes('w2s') }"
              @click="() => setTaxForms('w2s')"
            >
              W-2s
            </label>
          </div>
          <div class="col">
            <label
              class="btn w-100 btn-white"
              :class="{ active: taxForms.includes('1040s') }"
              @click="() => setTaxForms('1040s')"
            >
              1040s
            </label>
          </div>
        </div>
      </div>

      <div class="section" v-if="productsIncludeManage">
        <label class="mb-1">Enable Apps</label>
        <p class="small text-muted mb-3">Select which apps to enable in the Payments Hub.</p>
        <div class="btn-group-toggle row gx-2 mb-4">
          <div v-for="app in paymentsHubAppOptions" :key="app.name" class="col-12 my-1">
            <PaymentsHubAppSelector
              :app="app"
              :enabledApps="paymentsHubApps"
              @toggle="togglePaymentsHubApp"
            />
          </div>
        </div>
      </div>

      <div class="section" v-if="!productsIncludeManage">
        <label class="mb-1">Starting Screen</label>
        <p class="small text-muted mb-3">The first screen the user will see.</p>
        <div class="btn-group-toggle row gx-2 mb-4">
          <div :class="startingScreenColClass">
            <label
              class="btn w-100 btn-white"
              @click="() => setDeeplinkStep(productsIncludeDeposit ? 'welcome' : undefined)"
              :class="{ active: !deeplink.step || deeplink.step === 'welcome' }"
            >
              Welcome
            </label>
          </div>
          <div :class="startingScreenColClass">
            <label
              class="btn w-100 btn-white"
              @click="() => setDeeplinkStep('search-company')"
              :class="{ active: deeplink.step === 'search-company' }"
            >
              Search
            </label>
          </div>
          <div :class="startingScreenColClass">
            <label
              class="btn w-100 btn-white"
              @click="() => setDeeplinkStep('login-company')"
              :class="{ active: deeplink.step === 'login-company' }"
            >
              Login
            </label>
          </div>
          <div :class="startingScreenColClass" v-if="productsIncludeDeposit">
            <label
              class="btn w-100 btn-white"
              @click="() => setDeeplinkStep('direct-deposit-setup')"
              :class="{ active: deeplink.step === 'direct-deposit-setup' }"
            >
              DD Setup
            </label>
          </div>
        </div>
        <div v-if="deeplink.step === 'login-company'" class="deeplink-settings">
          <template v-if="isScopePayLink">
            <div class="form-group">
              <h6 class="header-pretitle mb-2">Company</h6>
              <v-select
                input-id="hello"
                v-model="deeplink.companyId"
                :options="payLinkCompanyOptions"
                :reduce="(company) => company._id"
                :get-option-label="(company) => company.name"
                placeholder="Select a company"
              />
            </div>

            <CustomSwitch
              v-if="productsIncludeSwitch"
              :value="deeplink.singleSwitch"
              helperText="Optimize the user flow for a single payment switch."
              label="Single Switch"
              @input="setSingleSwitch"
            />
          </template>
          <div class="form-group" v-else>
            <h6 class="header-pretitle mb-2">Company ID</h6>
            <input
              class="form-control"
              placeholder="5ec2fbf4398fed000810a42c"
              type="text"
              v-model="deeplink.companyId"
            />
          </div>
        </div>
      </div>

      <div class="section" v-if="showSearchRulesSection">
        <label class="mb-1">Search Experiences</label>
        <div>
          <p class="small text-muted mb-3">Select a search experience to preview.</p>
          <v-select
            v-model="selectedSearchRule"
            :options="filteredSearchRules"
            :reduce="(rule) => rule._id"
            :get-option-label="
              (rule) => rule.name || `Rule ${filteredSearchRules.indexOf(rule) + 1}`
            "
            placeholder="Select a search rule"
            @input="onSearchRuleSelected"
          />
        </div>
      </div>

      <div class="section">
        <label class="mb-1">Language</label>
        <p class="small text-muted mb-3">Language used for copy throughout Transact.</p>
        <div class="btn-group-toggle row gx-2 mb-4">
          <div class="col" v-for="language in languages" :key="language.code">
            <label
              class="btn w-100 btn-white"
              @click="() => selectLanguage(language.code)"
              :class="{ active: settings.language === language.code }"
            >
              {{ language.flag }} {{ language.title }}
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="featureUniqueForAtomic && !hasUserLinkContinuousAccessEnabled && !isScopePayLink"
        class="section"
      >
        <label class="mb-1">Demo</label>
        <p class="small text-muted mb-3">
          Emulate different authentication methods for supported companies.
        </p>
        <CustomSwitch :value="useUplinkDemo" label="TrueAuth" @input="toggleUseUplinkDemo" />
        <CustomSwitch
          :value="useTrueAuthDesktopDemo"
          label="TrueAuth Desktop"
          @input="toggleUseTrueAuthDesktopDemo"
        />
        <CustomSwitch :value="useCoAuthDemo" label="CoAuth" @input="setUseCoAuthDemo" />
        <transition name="expand">
          <Card v-if="useCoAuthDemo">
            <label class="card-header-title mb-3">CoAuth Variations</label>
            <CustomSwitch
              :value="coAuthDemo.identity"
              label="Identity Data"
              helperText="Simulate pre-populated identity data"
              @input="mergeCoAuthDemo({ identity: $event })"
              :class="{ 'mb-0': coAuthDemo.identity }"
            />
            <input
              v-if="coAuthDemo.identity"
              type="tel"
              class="form-control mb-3"
              placeholder="MFA Phone Number"
              :value="coAuthDemo.phoneNumber"
              @input="mergeCoAuthDemo({ phoneNumber: $event.target.value })"
            />
            <CustomSwitch
              :value="coAuthDemo.preScreenPrompt"
              label="Pre-Screen Prompt"
              helperText="Prompt the user to use a pre-screened income source"
              @input="mergeCoAuthDemo({ preScreenPrompt: $event })"
            />
            <CustomSwitch
              :value="coAuthDemo.preScreenSearchTile"
              label="Show Search Tile"
              helperText="Show a pre-screened income source on the search page"
              @input="mergeCoAuthDemo({ preScreenSearchTile: $event })"
            />
          </Card>
        </transition>
        <!-- user identity? pre-screen? -->
      </div>
    </div>
  </div>
</template>

<script>
import { ShuffleIcon } from 'vue-feather-icons'
import { featureUniqueForAtomic } from '@/lib/customer-customizations'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import { PRODUCTS, SCOPES } from '@atomicfi/constants-shared'
import CustomSwitch from '@/components/MainContent/CustomSwitch'
import Card from '@/components/MainContent/Card'
import EmulatorProductSelector from '@/components/Modules/Emulator/EmulatorProductSelector'
import EmulatorSectionHeader from '@/components/Modules/Emulator/EmulatorSectionHeader'
import PaymentsHubAppSelector from '@/components/Modules/Emulator/PaymentsHubAppSelector'

/** Options for selecting a deeplink company for PayLink */
const PAY_LINK_COMPANY_OPTIONS = {
  AMAZON: {
    _id: '65272c415d8a530008e972df',
    name: 'Amazon',
  },
  NETFLIX: {
    _id: '64ecca15ec669e000851d5d2',
    name: 'Netflix',
  },
  APPLE: {
    _id: '6536d5456f184e00095f9df8',
    name: 'Apple',
  },
  WALMART: {
    _id: '652802875d8a530008e9e410',
    name: 'Walmart',
  },
}

export default {
  name: 'EmulatorInitialization',
  components: {
    Card,
    CustomSwitch,
    EmulatorProductSelector,
    EmulatorSectionHeader,
    PaymentsHubAppSelector,
    ShuffleIcon,
  },
  props: {
    searchRule: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    availableProducts: [],
    distribution: {
      type: 'total',
      amount: undefined,
      action: 'create',
    },
    selectedSearchRule: null,
  }),
  watch: {
    products: {
      handler() {
        if (this.selectedSearchRule) {
          const rule = this.filteredSearchRules.find((rule) => rule._id === this.selectedSearchRule)
          if (!rule) {
            this.selectedSearchRule = null
            this.$emit('update:searchRule', null)
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('emulator', [
      'deeplink',
      'paymentsHubApps',
      'taxForms',
      'languages',
      'products',
      'scope',
      'settings',
      'useMultipleAccounts',
      'useUplinkDemo',
      'useTrueAuthDesktopDemo',
      'useCoAuthDemo',
      'coAuthDemo',
    ]),
    ...mapState('customer', ['activeCustomer']),
    ...mapGetters('customer', ['scopes', 'hasUserLinkContinuousAccessEnabled']),
    isScopePayLink() {
      return this.scope?.name === SCOPES.PAY_LINK
    },
    productsIncludeDeposit() {
      return this.products.includes(PRODUCTS.DEPOSIT)
    },
    startingScreenColClass() {
      // deposit/userlink shows 2x2 grid, paylink has buttons on same row
      return this.productsIncludeDeposit ? 'col-6 my-1' : 'col my-1'
    },
    productsIncludeTax() {
      return this.products.includes('tax')
    },
    productsIncludeSwitch() {
      return this.products.includes(PRODUCTS.SWITCH)
    },
    productsIncludeManage() {
      return this.products.includes('manage')
    },
    paymentsHubAppOptions() {
      return [
        { title: 'Expenses', name: 'expenses' },
        { title: 'Suggestions', name: 'suggestions' },
        { title: 'Pay Now', name: 'pay-now', disabled: true },
        { title: 'Orders', name: 'orders', disabled: true },
      ]
    },
    hasSearchRules() {
      return this.filteredSearchRules.length > 0
    },

    showSearchRulesSection() {
      return (
        this.products.length === 1 &&
        this.filteredSearchRules.some((rule) => rule.product === this.products[0])
      )
    },

    filteredSearchRules() {
      if (!this.products.length) return []
      return (
        this.activeCustomer?.features?.searchRules?.filter(
          (rule) => rule.product === this.products[0]
        ) || []
      )
    },
    payLinkCompanyOptions() {
      return Object.values(PAY_LINK_COMPANY_OPTIONS)
    },
  },
  methods: {
    ...mapMutations('emulator', [
      'setProducts',
      'setPaymentsHubApps',
      'setTaxForms',
      'setUseMultipleAccounts',
      'setScope',
      'setUseUplinkDemo',
      'setUseTrueAuthDesktopDemo',
      'setUseCoAuthDemo',
      'mergeCoAuthDemo',
    ]),
    ...mapActions('emulator', [
      'resetDeeplink',
      'updateLanguage',
      'updateTaxFormType',
      'updateSearchRule',
      'updateDeeplink',
    ]),
    featureUniqueForAtomic,
    setDeeplinkStep(deeplinkStep) {
      const updatedDeeplink = {
        ...this.deeplink,
        step: deeplinkStep,
      }

      if (this.isScopePayLink && deeplinkStep === 'login-company') {
        // Auto-select first company from dropdown for PayLink
        if (!updatedDeeplink.companyId) {
          updatedDeeplink.companyId = this.payLinkCompanyOptions[0]._id
        }
        // Default to single switch for Switch product if not already set
        if (this.productsIncludeSwitch && updatedDeeplink.singleSwitch === undefined) {
          updatedDeeplink.singleSwitch = true
        }
      }

      // Unset single switch if not deep linking to company, unless the user
      // has explicitly set it to false.
      if (deeplinkStep !== 'login-company' && updatedDeeplink.singleSwitch) {
        updatedDeeplink.singleSwitch = undefined
      }

      this.updateDeeplink(updatedDeeplink)

      this.$analytics.track({
        event: 'Set Emulator Deeplink Step',
        payload: {
          deeplinkStep,
        },
      })
    },
    setSingleSwitch(value) {
      this.updateDeeplink({
        ...this.deeplink,
        singleSwitch: value,
      })
    },
    setTaxForms(taxFormType) {
      const taxForm = [taxFormType]

      this.$analytics.track({
        event: 'Set Emulator Tax Form Type',
        payload: {
          taxFormType,
        },
      })

      this.updateTaxFormType(taxForm)
    },
    toggleProduct(productToToggle) {
      let products = this.products
      if (products.includes(productToToggle.name)) {
        products = products.filter((product) => product !== productToToggle.name)

        this.$analytics.track({
          event: 'Remove Emulator Product',
          payload: {
            productRemoved: productToToggle.name,
          },
        })
      } else {
        if (productToToggle.name === 'manage') {
          products = ['manage']
        } else {
          products = products.filter((product) => product !== 'manage')
          products.push(productToToggle.name)
        }

        this.$analytics.track({
          event: 'Add Emulator Product',
          payload: {
            productAdded: productToToggle.name,
          },
        })
      }

      this.setProducts(products)
    },
    togglePaymentsHubApp(app) {
      let apps = [...this.paymentsHubApps]
      if (apps.includes(app.name)) {
        apps = apps.filter((a) => a !== app.name)
      } else {
        apps.push(app.name)
      }
      this.setPaymentsHubApps(apps)
    },
    selectLanguage(language) {
      this.updateLanguage({ analytics: this.$analytics, language })
    },
    toggleUseMultipleAccounts(value) {
      this.setUseMultipleAccounts(value)
    },
    toggleUseUplinkDemo(value) {
      this.setUseUplinkDemo(value)
    },
    toggleUseTrueAuthDesktopDemo(value) {
      this.setUseTrueAuthDesktopDemo(value)
    },
    removeScope() {
      this.setScope(undefined)
      this.setProducts([])
      this.resetDeeplink()
    },
    onSearchRuleSelected(ruleId) {
      this.$emit('update:searchRule', ruleId)
      this.updateSearchRule(ruleId)
    },
  },
  created() {
    if (this.scope.name === SCOPES.PAY_LINK) {
      this.toggleUseUplinkDemo(true)
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-control-label {
  font-size: 0.8rem;
  padding-top: 3px;
}
.scope-option {
  position: relative;
  display: inline-block;
  text-align: left;
  font-size: 18px;
  padding: 20px;
  margin-bottom: 15px;
  img {
    width: 30px;
    margin-right: 10px;
  }
  span {
    position: relative;
    top: 2px;
  }
  .scope-button-icon {
    position: absolute;
    right: 20px;
    bottom: 26px;
  }
}
.section {
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.deeplink-settings {
  margin-left: 8px;
}

:deep(.v-select) {
  .vs__dropdown-toggle {
    padding: 4px 0;
    border-color: #dee2e6;
  }

  .vs__selected {
    margin: 0 2px;
  }

  .vs__search {
    margin: 0;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
}

.expand-enter,
.expand-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
